import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Button, Card } from '../../shared/components';
import { ApiError } from '../../utils/api';
import logo from '../../assets/logo.svg';
import './Login.css';

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [error, setError] = React.useState('');
  const [submitting, setSubmitting] = React.useState(false);

  async function handleSubmit(event) {
    event.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      await login(email, password);
      navigate('/');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Unable to log in right now.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="landing-page">
      <section className="landing-hero">
        <img src={logo} alt="Series 65 Exam Prep" className="landing-logo" />
        <h1 className="landing-headline">
          Free Series 65 Exam Prep for Aspiring Investment Adviser Representatives
        </h1>
        <p className="landing-subhead">
          Practice questions, flashcards, a full-length timed exam, and the actual law behind the
          Uniform Investment Adviser Law Examination &mdash; all in one place. Logging in also enables
          progress tracking, bookmarking, highlighting, and AI-powered explanations on the study guide.
          Create your free account to get started.
        </p>

        <Card className="auth-card">
          <h2>Log in</h2>
          <form onSubmit={handleSubmit}>
            <label htmlFor="email">Email</label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />

            <label htmlFor="password">Password</label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />

            {error && <p className="auth-error">{error}</p>}

            <Button type="submit" disabled={submitting}>
              {submitting ? 'Logging in...' : 'Log in'}
            </Button>
          </form>
          <p className="auth-switch">
            Don&apos;t have an account? <Link to="/register">Sign up</Link>
          </p>
        </Card>

        <a href="#about-the-exam" className="landing-scroll-cue">
          <span>What is the Series 65, and why does it matter?</span>
          <span className="landing-scroll-arrow" aria-hidden="true">&darr;</span>
        </a>
      </section>

      <section className="landing-info" id="about-the-exam">
        <div className="landing-info-block">
          <h2>What is the Series 65 exam?</h2>
          <p>
            The Series 65 &mdash; officially the Uniform Investment Adviser Law Examination &mdash; is
            developed by NASAA and is the exam most states require before you can register as an
            Investment Adviser Representative (IAR). It tests four areas: economic factors and
            business information, investment vehicle characteristics, client investment
            recommendations and strategies, and the laws, regulations, and ethical obligations that
            govern how advisers and their representatives actually do business.
          </p>
        </div>

        <div className="landing-info-block">
          <h2>Why you actually need to study for it</h2>
          <p>
            This isn&apos;t a rubber-stamp exam. It&apos;s 140 questions in 180 minutes, and you need
            to answer at least 92 of the 130 scored questions correctly &mdash; about 71% &mdash; to
            pass. A meaningful chunk of that is dense securities law most candidates have never read
            before: the Uniform Securities Act, the Investment Advisers Act of 1940, and NASAA&apos;s
            model rules on custody, recordkeeping, and unethical business practices. Fail, and you&apos;re
            looking at a mandatory 30-day wait before your next attempt (180 days after a third
            failure) &mdash; time you&apos;re not earning as a registered IAR.
          </p>
        </div>

        <div className="landing-info-block">
          <h2>Everything you need to pass, in one place</h2>
          <ul className="landing-feature-list">
            <li>
              <strong>Practice questions by topic</strong> &mdash; written in the same style and
              format as the real exam, weighted to match how heavily each subject is actually tested.
            </li>
            <li>
              <strong>Flashcards</strong> &mdash; key terms and definitions across every topic, with
              known/needs-review tracking.
            </li>
            <li>
              <strong>A full-length timed practice exam</strong> &mdash; 90 minutes, questions drawn
              proportionally by topic weight, with a real pass/fail score and section breakdown.
            </li>
            <li>
              <strong>A complete study guide</strong> &mdash; the actual text of the Uniform Securities
              Act, the Investment Advisers Act of 1940, and the NASAA model rules the exam draws from,
              readable and highlightable so you can study the primary source, not just someone&apos;s
              summary of it.
            </li>
            <li>
              <strong>AI-powered explanations</strong> &mdash; highlight any confusing passage in the
              study guide, or review any question you missed on a quiz or practice exam, and get an
              instant, plain-English explanation of why the right answer is right &mdash; using your
              own free Gemini or Groq API key.
            </li>
            <li>
              <strong>Progress tracking</strong> &mdash; so you know which topics are actually ready
              and which still need work before you sit for the real thing.
            </li>
          </ul>
        </div>

        <div className="landing-info-cta">
          <p>Ready to start studying for the Series 65?</p>
          <Link to="/register">
            <Button>Create your free account</Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
